import React from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Trash2, CalendarClock, Send, CircleStop } from 'lucide-react';
import * as api from '../services/vcNoticeApi';
import CustomersNoticeScheduleDates from './CustomersNoticeScheduleDates';
import CustomersNoticeFmtBar from './CustomersNoticeFmtBar';
import CustomersNoticeOryaCompose from './CustomersNoticeOryaCompose';
import CustomersNoticeTip from './CustomersNoticeTip';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import {
  hydrateNoticeHtml,
  serializeNoticeHtml,
  normalizePasteHtml,
  normalizePastePlainText,
  parseYoutubeId,
  youtubeBlockHtml,
  toLocalInputValue,
  fromLocalInputValue,
  isFutureLocalPublishAt,
} from '../utils/noticeRichUtils';
import { clampFixedPanelCoords } from '../utils/noticeFloatingPanel';
import { findDisplayWindowConflict } from '../utils/noticeScheduleConflict';

export default class CustomersNoticeEditor extends React.Component {
  state = {
    title: '',
    status: 'draft',
    publishAt: '',
    expiresAt: '',
    saved: '',
    slash: null,
    fmt: null,
    lineHint: null,
    oryaCompose: null,
    imgBox: null,
    ytPrompt: false,
    ytUrl: '',
    ytError: null,
    busy: null,
    error: null,
    scheduledSlots: [],
  };

  editorRef = React.createRef();
  docScrollRef = React.createRef();
  loaded = false;
  saveTimer = null;
  img = null;
  _imgGuids = new Set();

  componentDidMount() {
    this.hydrateFromNotice();
    this.loadScheduledSlots();
    this._onSelectionChange = () => {
      if (this._fmtSelRaf) cancelAnimationFrame(this._fmtSelRaf);
      this._fmtSelRaf = requestAnimationFrame(() => {
        this._fmtSelRaf = null;
        this.syncFmtFromSelection();
        this.syncLineHint();
      });
    };
    document.addEventListener('selectionchange', this._onSelectionChange);
    this._onDocScroll = () => this.syncLineHint();
    requestAnimationFrame(() => {
      this.docScrollRef.current?.addEventListener('scroll', this._onDocScroll, { passive: true });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.notice?.id !== this.props.notice?.id) {
      this.loaded = false;
      this.hydrateFromNotice();
      this.loadScheduledSlots();
    } else if (
      prevProps.notice?.status !== this.props.notice?.status ||
      prevProps.notice?.publishAt !== this.props.notice?.publishAt ||
      prevProps.notice?.expiresAt !== this.props.notice?.expiresAt
    ) {
      this.setState({
        status: this.props.notice.status || 'draft',
        publishAt: toLocalInputValue(this.props.notice.publishAt),
        expiresAt: toLocalInputValue(this.props.notice.expiresAt),
      });
    }
    this.syncEditor();
    this.syncSlashOutsideListener(prevState.slash, this.state.slash);
  }

  componentWillUnmount() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    document.removeEventListener('selectionchange', this._onSelectionChange);
    this.docScrollRef.current?.removeEventListener('scroll', this._onDocScroll);
    if (this._fmtSelRaf) cancelAnimationFrame(this._fmtSelRaf);
    if (this._fmtSelTimer) clearTimeout(this._fmtSelTimer);
    if (this._lineHintTimer) clearTimeout(this._lineHintTimer);
    this.unbindImgTrack();
    this.unbindSlashOutside();
  }

  syncSlashOutsideListener = (prevSlash, nextSlash) => {
    if (nextSlash && !prevSlash) {
      requestAnimationFrame(() => {
        if (this.state.slash) this.bindSlashOutside();
      });
    } else if (!nextSlash && prevSlash) {
      this.unbindSlashOutside();
    }
  };

  bindSlashOutside = () => {
    if (this._slashOutsideBound) return;
    this._slashOutsideBound = true;
    this._onSlashOutside = (event) => {
      const t = event.target;
      if (t instanceof Element && t.closest('.cmc-manual-slash')) return;
      if (this.state.slash) this.setState({ slash: null });
    };
    document.addEventListener('mousedown', this._onSlashOutside, true);
  };

  unbindSlashOutside = () => {
    if (!this._slashOutsideBound) return;
    this._slashOutsideBound = false;
    if (this._onSlashOutside) {
      document.removeEventListener('mousedown', this._onSlashOutside, true);
      this._onSlashOutside = null;
    }
  };

  hydrateFromNotice() {
    const n = this.props.notice;
    if (!n) return;
    this._imgGuids = new Set((n.imagens || []).map((i) => i.guid).filter(Boolean));
    this.collectImgGuids(n.contentHtml || '').forEach((g) => this._imgGuids.add(g));
    this.setState({
      title: n.title || '',
      status: n.status || 'draft',
      publishAt: toLocalInputValue(n.publishAt),
      expiresAt: toLocalInputValue(n.expiresAt),
      saved: '',
      error: null,
    });
    this.loaded = false;
    this.syncEditor();
  }

  loadScheduledSlots = async () => {
    const selfId = this.props.notice?.id;
    try {
      const data = await api.listNotices('todos');
      const scheduledSlots = (data.items || [])
        .filter(
          (item) =>
            item.id !== selfId &&
            item.publishAt &&
            (item.status === 'scheduled' || item.status === 'published')
        )
        .map((item) => {
          const local = toLocalInputValue(item.publishAt);
          return {
            id: item.id,
            title: item.title || 'Sem título',
            status: item.status,
            publishAt: item.publishAt,
            expiresAt: item.expiresAt || null,
            localKey: local ? local.slice(0, 16) : null,
          };
        })
        .filter((item) => item.localKey);
      this.setState({ scheduledSlots });
    } catch (_) {
      this.setState({ scheduledSlots: [] });
    }
  };

  findScheduleConflict = (localPublishAt, localExpiresAt) => {
    const start = localPublishAt ? new Date(localPublishAt) : null;
    if (!start || Number.isNaN(start.getTime())) return null;
    const end = localExpiresAt ? new Date(localExpiresAt) : null;
    return findDisplayWindowConflict(
      { publishAt: start, expiresAt: end && !Number.isNaN(end.getTime()) ? end : null },
      this.state.scheduledSlots || []
    );
  };

  syncEditor() {
    const el = this.editorRef.current;
    const n = this.props.notice;
    if (!el || !n || this.loaded) return;
    el.innerHTML = hydrateNoticeHtml(n.contentHtml || '');
    this.clearInlineFontStyles(el);
    this.loaded = true;
  }

  editorHtml() {
    const el = this.editorRef.current;
    return el ? serializeNoticeHtml(el.innerHTML) : '';
  }

  collectImgGuids = (htmlOrEl) => {
    const set = new Set();
    if (htmlOrEl && htmlOrEl.querySelectorAll) {
      htmlOrEl.querySelectorAll('img[data-img]').forEach((img) => {
        const g = img.getAttribute('data-img');
        if (g) set.add(g);
      });
      return set;
    }
    String(htmlOrEl || '').replace(/data-img\s*=\s*["']([^"']+)["']/gi, (_, g) => {
      if (g) set.add(g);
      return '';
    });
    return set;
  };

  syncRemovedImages = (el) => {
    const now = this.collectImgGuids(el);
    const removed = [];
    this._imgGuids.forEach((g) => {
      if (!now.has(g)) removed.push(g);
    });
    this._imgGuids = now;
    removed.forEach((guid) => {
      api.deleteNoticeImage(this.props.notice.id, guid).catch(() => {});
    });
  };

  flushSave = async ({ quiet = false } = {}) => {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    const el = this.editorRef.current;
    const n = this.props.notice;
    if (!el || !n) return;
    this.normalizeEditorInlineFormats();
    const html = this.editorHtml();
    this.syncRemovedImages(el);
    try {
      await api.updateNotice(n.id, { title: this.state.title, html });
      if (!quiet) this.setState({ saved: 'Salvo agora' });
    } catch (e) {
      if (!quiet) this.setState({ saved: 'Erro ao salvar', error: e.message });
    }
  };

  scheduleSave = () => {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.flushSave(), 800);
    this.setState({ saved: 'Salvando…' });
  };

  onTitleChange = (e) => {
    this.setState({ title: e.target.value });
    this.scheduleSave();
  };

  onEditorInput = () => {
    const el = this.editorRef.current;
    if (!el) return;
    this.syncRemovedImages(el);
    this.scheduleSave();
  };

  selectionCaretRect = (range) => {
    if (!range) return null;
    let rect = range.getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) return rect;
    const rects = range.getClientRects();
    for (let i = 0; i < rects.length; i++) {
      if (rects[i].width > 0 || rects[i].height > 0) return rects[i];
    }
    const node = range.commonAncestorContainer;
    const block = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return block && block.getBoundingClientRect ? block.getBoundingClientRect() : rect;
  };

  selectionInEditor = (range, el) => {
    if (!range || !el) return false;
    const node = range.commonAncestorContainer;
    if (el === node || el.contains(node)) return true;
    try {
      return el.contains(range.startContainer) && el.contains(range.endContainer);
    } catch (_) {
      return false;
    }
  };

  blockTextClean = (node) =>
    String(node?.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\u200b/g, '')
      .trim();

  findCaretBlock = (range, el) => {
    if (!range || !el) return null;
    let block = range.startContainer;
    if (block.nodeType === Node.TEXT_NODE) block = block.parentElement;
    while (block && block !== el) {
      const tag = String(block.tagName || '').toUpperCase();
      if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'LI', 'BLOCKQUOTE', 'PRE'].includes(tag)) {
        return block;
      }
      block = block.parentElement;
    }
    return el;
  };

  isEditorVisuallyEmpty = (el) => {
    if (!el) return true;
    if (el.querySelector('img, .cmc-notice-yt, iframe, video')) return false;
    return this.blockTextClean(el) === '';
  };

  isCaretOnEmptyLine = () => {
    const el = this.editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.isCollapsed || !sel.rangeCount) return false;
    if (this.state.oryaCompose || this.state.slash || this.state.fmt || this.state.ytPrompt) return false;
    const range = sel.getRangeAt(0);
    if (!this.selectionInEditor(range, el)) return false;
    const block = this.findCaretBlock(range, el);
    if (!block) return false;
    if (block.closest?.('.cmc-notice-yt, [data-img-guid], [data-img-uploading]')) return false;
    if (block !== el && block.querySelector?.('img, .cmc-notice-yt, iframe')) return false;
    return this.blockTextClean(block) === '';
  };

  syncLineHint = () => {
    if (this.state.oryaCompose || this.state.slash || this.state.fmt) {
      if (this.state.lineHint) this.setState({ lineHint: null });
      return;
    }
    const el = this.editorRef.current;
    if (!el || this.isEditorVisuallyEmpty(el) || !this.isCaretOnEmptyLine()) {
      if (this.state.lineHint) this.setState({ lineHint: null });
      return;
    }
    const sel = window.getSelection();
    if (!sel?.rangeCount) {
      if (this.state.lineHint) this.setState({ lineHint: null });
      return;
    }
    const r = this.selectionCaretRect(sel.getRangeAt(0));
    if (!r) {
      if (this.state.lineHint) this.setState({ lineHint: null });
      return;
    }
    const next = { top: r.top, left: r.left + 2 };
    const prev = this.state.lineHint;
    if (prev && Math.abs(prev.top - next.top) < 1 && Math.abs(prev.left - next.left) < 1) return;
    this.setState({ lineHint: next });
  };

  openOryaCompose = () => {
    const el = this.editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    try {
      this._oryaRange = range.cloneRange();
    } catch (_) {
      this._oryaRange = null;
    }
    const r = this.selectionCaretRect(range);
    if (!r) return;
    const preferred = clampFixedPanelCoords({
      preferredTop: r.bottom + 8,
      preferredLeft: r.left,
      width: 360,
      height: 360,
    });
    this.setState({
      oryaCompose: {
        top: preferred.top,
        left: preferred.left,
      },
      slash: null,
      fmt: null,
      lineHint: null,
    });
  };

  closeOryaCompose = () => {
    this._oryaRange = null;
    this.setState({ oryaCompose: null }, () => this.syncLineHint());
  };

  /** Remove overrides de fonte que o browser/Orya podem colar (ex.: 12.5px do painel). */
  clearInlineFontStyles = (root) => {
    if (!root) return;
    root.querySelectorAll('[style]').forEach((node) => {
      node.style.removeProperty('font');
      node.style.removeProperty('font-size');
      node.style.removeProperty('font-family');
      node.style.removeProperty('font-weight');
      node.style.removeProperty('font-style');
      node.style.removeProperty('line-height');
      node.style.removeProperty('color');
      node.style.removeProperty('letter-spacing');
      if (!String(node.getAttribute('style') || '').trim()) node.removeAttribute('style');
    });
    root.querySelectorAll('font').forEach((node) => {
      const parent = node.parentNode;
      if (!parent) return;
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
    });
  };

  /** Insere texto da Orya sem estilos — mesmo tipografia do editor. */
  insertPlainEditorText = (text, { replaceSelection = false } = {}) => {
    const value = String(text || '');
    if (!value) return false;
    const el = this.editorRef.current;
    if (!el) return false;
    el.focus({ preventScroll: true });
    const sel = window.getSelection();
    if (!sel) return false;

    if (replaceSelection) {
      this.restoreFmtSelection();
    } else if (this._oryaRange) {
      try {
        sel.removeAllRanges();
        sel.addRange(this._oryaRange);
      } catch (_) {
        /* range may be stale */
      }
    }
    if (!sel.rangeCount) return false;

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const parts = value
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split(/\n+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const frag = document.createDocumentFragment();
    let last = null;
    if (parts.length <= 1) {
      last = document.createTextNode(parts[0] || value);
      frag.appendChild(last);
    } else {
      parts.forEach((part) => {
        const block = document.createElement('div');
        block.textContent = part;
        frag.appendChild(block);
        last = block;
      });
    }
    range.insertNode(frag);
    if (last) {
      const after = document.createRange();
      after.setStartAfter(last);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    }
    this.normalizeEditorInlineFormats();
    this.clearInlineFontStyles(el);
    this.onEditorInput();
    return true;
  };

  insertOryaComposeText = (text) => {
    this.insertPlainEditorText(text, { replaceSelection: false });
    this._oryaRange = null;
    this.setState({ oryaCompose: null }, () => this.syncLineHint());
  };

  findLinkInSelection = (range, el) => {
    if (!range || !el) return null;
    const fromNode = (node) => {
      let cur = node;
      if (cur && cur.nodeType === Node.TEXT_NODE) cur = cur.parentElement;
      while (cur && cur !== el) {
        if (cur.tagName === 'A') {
          const href = cur.getAttribute('href');
          return href ? { href, el: cur } : null;
        }
        cur = cur.parentElement;
      }
      return null;
    };
    return (
      fromNode(range.commonAncestorContainer) ||
      fromNode(range.startContainer) ||
      fromNode(range.endContainer)
    );
  };

  queueSyncFmt = (delayMs = 0) => {
    if (this._fmtSelRaf) cancelAnimationFrame(this._fmtSelRaf);
    if (this._fmtSelTimer) clearTimeout(this._fmtSelTimer);
    const run = () => {
      this._fmtSelRaf = requestAnimationFrame(() => {
        this._fmtSelRaf = null;
        this.syncFmtFromSelection();
      });
    };
    if (delayMs > 0) this._fmtSelTimer = setTimeout(run, delayMs);
    else run();
  };

  syncFmtFromSelection = () => {
    const el = this.editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.rangeCount) {
      if (!this._fmtSelecting && this.state.fmt) {
        this.setState({ fmt: null });
        this._fmtRange = null;
      }
      return;
    }
    if (sel.isCollapsed) {
      // Durante o arraste a seleção pode colapsar momentaneamente — não esconder ainda
      if (this._fmtSelecting) return;
      if (this.state.fmt) {
        this.setState({ fmt: null });
        this._fmtRange = null;
      }
      return;
    }
    const range = sel.getRangeAt(0);
    const text = String(sel.toString() || '').replace(/\u00a0/g, ' ').trim();
    if (!this.selectionInEditor(range, el) || !text) {
      if (!this._fmtSelecting && this.state.fmt) {
        this.setState({ fmt: null });
        this._fmtRange = null;
      }
      return;
    }
    const r = this.selectionCaretRect(range);
    if (!r) {
      this.queueSyncFmt(16);
      return;
    }
    try {
      this._fmtRange = range.cloneRange();
    } catch (_) {
      this._fmtRange = null;
    }
    const barW = 420;
    const linkInfo = this.findLinkInSelection(range, el);
    const next = {
      top: Math.max(8, r.top - 42),
      left: Math.max(
        16,
        Math.min(
          r.left + Math.max(r.width, 1) / 2 - barW / 2,
          window.innerWidth - barW - 16
        )
      ),
      text,
      linkHref: linkInfo?.href || '',
    };
    const prev = this.state.fmt;
    if (
      prev &&
      Math.abs(prev.top - next.top) < 1 &&
      Math.abs(prev.left - next.left) < 1 &&
      prev.text === next.text &&
      (prev.linkHref || '') === (next.linkHref || '')
    ) {
      return;
    }
    this.setState({ fmt: next, slash: null });
  };

  onEditorPointerDown = () => {
    this._fmtSelecting = true;
  };

  onEditorPointerUp = () => {
    this._fmtSelecting = false;
    this.queueSyncFmt(0);
    // Triple-click / seleção por palavra às vezes finaliza após o pointerup
    this.queueSyncFmt(32);
  };

  onEditorKeyDown = (e) => {
    if (e.key === ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      if (!this.state.oryaCompose && this.isCaretOnEmptyLine()) {
        e.preventDefault();
        this.openOryaCompose();
      }
    }
  };

  onEditorKey = (e) => {
    if (e.key === '/') {
      const sel = window.getSelection();
      if (sel.rangeCount) {
        const r = this.selectionCaretRect(sel.getRangeAt(0));
        if (r) this.setState({ slash: { top: r.bottom + 6, left: r.left }, fmt: null, lineHint: null });
      }
    } else if (['Escape', 'Enter', ' '].indexOf(e.key) > -1) {
      if (this.state.slash) this.setState({ slash: null });
    }
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === 'a') {
        this.queueSyncFmt(0);
        this.queueSyncFmt(32);
        return;
      }
      if (k === 'c' || k === 'x' || k === 'z' || k === 'y') return;
    }
    if (
      e.key === 'Shift' ||
      e.key === 'Control' ||
      e.key === 'Alt' ||
      e.key === 'Meta' ||
      e.key.startsWith('Arrow') ||
      e.key === 'Home' ||
      e.key === 'End'
    ) {
      if (e.shiftKey && (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End')) {
        this.queueSyncFmt(0);
      }
      this.syncLineHint();
      return;
    }
    this.onEditorInput();
    this.syncLineHint();
  };

  onEditorClick = (e) => {
    if (e.target?.closest?.('[data-img-uploading]')) {
      if (this.state.imgBox) this.clearImgSelection();
      return;
    }
    const yt = e.target?.closest?.('.cmc-notice-yt[data-yt]');
    if (yt && this.editorRef.current?.contains(yt)) {
      this.img = yt;
      this.measure();
      this.bindImgTrack();
      return;
    }
    if (e.target && e.target.tagName === 'IMG' && e.target.getAttribute('data-img')) {
      this.img = e.target;
      this.measure();
      this.bindImgTrack();
    } else if (this.state.imgBox) {
      this.clearImgSelection();
    }
  };

  clearImgSelection = () => {
    this.img = null;
    this.unbindImgTrack();
    if (this.state.imgBox) this.setState({ imgBox: null });
  };

  measure = () => {
    if (!this.img || !this.img.isConnected) {
      this.clearImgSelection();
      return;
    }
    const r = this.img.getBoundingClientRect();
    this.setState({ imgBox: { top: r.top, left: r.left, w: r.width, h: r.height } });
  };

  bindImgTrack = () => {
    if (this._imgTrackBound) return;
    this._imgTrackBound = true;
    this._onImgTrack = () => {
      if (this._imgTrackRaf) cancelAnimationFrame(this._imgTrackRaf);
      this._imgTrackRaf = requestAnimationFrame(() => this.measure());
    };
    window.addEventListener('scroll', this._onImgTrack, true);
    window.addEventListener('resize', this._onImgTrack);
  };

  unbindImgTrack = () => {
    if (!this._imgTrackBound) return;
    this._imgTrackBound = false;
    if (this._imgTrackRaf) cancelAnimationFrame(this._imgTrackRaf);
    if (this._onImgTrack) {
      window.removeEventListener('scroll', this._onImgTrack, true);
      window.removeEventListener('resize', this._onImgTrack);
    }
  };

  exec(cmd, val) {
    this.restoreFmtSelection();
    document.execCommand(cmd, false, val || null);
    this.normalizeEditorInlineFormats();
    this.onEditorInput();
    this.setState({ fmt: null, slash: null });
    this._fmtRange = null;
  }

  restoreFmtSelection = () => {
    if (!this._fmtRange) return;
    const sel = window.getSelection();
    if (!sel) return;
    try {
      sel.removeAllRanges();
      sel.addRange(this._fmtRange);
    } catch (_) {
      /* range may be stale */
    }
  };

  replaceFmtSelection = (text) => {
    this.insertPlainEditorText(text, { replaceSelection: true });
    this.setState({ fmt: null });
    this._fmtRange = null;
  };

  insertFmtBelow = (text) => {
    const value = String(text || '').trim();
    if (!value) return;
    this.restoreFmtSelection();
    const sel = window.getSelection();
    const range = this._fmtRange || (sel?.rangeCount ? sel.getRangeAt(0) : null);
    if (!range) return;
    let block = range.endContainer;
    if (block.nodeType === Node.TEXT_NODE) block = block.parentElement;
    while (block && block !== this.editorRef.current) {
      const tag = String(block.tagName || '').toUpperCase();
      if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'LI', 'BLOCKQUOTE', 'PRE'].includes(tag)) break;
      block = block.parentElement;
    }
    const parts = value
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split(/\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const frag = document.createDocumentFragment();
    (parts.length ? parts : [value]).forEach((part) => {
      const p = document.createElement('div');
      p.textContent = part;
      frag.appendChild(p);
    });
    if (block && block.parentNode) {
      if (block.nextSibling) block.parentNode.insertBefore(frag, block.nextSibling);
      else block.parentNode.appendChild(frag);
    } else if (this.editorRef.current) {
      this.editorRef.current.appendChild(frag);
    }
    this.clearInlineFontStyles(this.editorRef.current);
    this.onEditorInput();
    this.setState({ fmt: null });
    this._fmtRange = null;
  };

  normalizeEditorInlineFormats = () => {
    const el = this.editorRef.current;
    if (!el) return;
    el.querySelectorAll('span[style], font[style], font[face], font[size], font[color]').forEach((node) => {
      const style = String(node.getAttribute('style') || '').toLowerCase();
      const fw = ((style.match(/font-weight\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const fs = ((style.match(/font-style\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const td = ((style.match(/text-decoration(?:-line)?\s*:\s*([^;]+)/) || [])[1] || '').trim();
      const wraps = [];
      if (/bold|[6-9]00/.test(fw)) wraps.push('b');
      if (/italic/.test(fs)) wraps.push('i');
      if (/underline/.test(td)) wraps.push('u');
      if (/line-through/.test(td)) wraps.push('s');
      const parent = node.parentNode;
      if (!parent) return;
      if (!wraps.length) {
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        return;
      }
      let wrap = document.createElement(wraps[0]);
      let innermost = wrap;
      for (let i = 1; i < wraps.length; i++) {
        const next = document.createElement(wraps[i]);
        innermost.appendChild(next);
        innermost = next;
      }
      while (node.firstChild) innermost.appendChild(node.firstChild);
      parent.insertBefore(wrap, node);
      parent.removeChild(node);
    });
  };

  insertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      input.remove();
      if (file) await this.uploadAndInsertImage(file);
    };
    document.body.appendChild(input);
    input.click();
  };

  onEditorPaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    let file = null;
    if (cd.files) {
      for (let i = 0; i < cd.files.length; i++) {
        if (String(cd.files[i].type || '').startsWith('image/')) {
          file = cd.files[i];
          break;
        }
      }
    }
    if (file) {
      e.preventDefault();
      this.uploadAndInsertImage(file);
      return;
    }
    e.preventDefault();
    const htmlRaw = cd.getData('text/html');
    const textRaw = cd.getData('text/plain');
    const insert =
      htmlRaw && /<[a-z][\s\S]*>/i.test(htmlRaw)
        ? normalizePasteHtml(htmlRaw)
        : normalizePastePlainText(textRaw);
    if (!insert) return;
    document.execCommand('insertHTML', false, insert);
    this.onEditorInput();
    this.setState({ slash: null, fmt: null });
  };

  uploadAndInsertImage = async (file) => {
    const el = this.editorRef.current;
    const n = this.props.notice;
    if (!el || !n || !file) return;
    const mime = String(file.type || '').toLowerCase().split(';')[0].trim();
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'].includes(mime)) {
      this.setState({ error: 'Tipo de imagem não permitido. Use JPG, PNG, WEBP ou GIF.' });
      return;
    }

    el.focus();
    const sel = window.getSelection();
    let range = null;
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) range = r.cloneRange();
    }

    const blobUrl = URL.createObjectURL(file);
    const placeholder = document.createElement('figure');
    placeholder.className = 'cmc-notice-img-uploading';
    placeholder.setAttribute('data-img-uploading', '1');
    placeholder.setAttribute('contenteditable', 'false');
    placeholder.setAttribute('aria-busy', 'true');
    placeholder.setAttribute('aria-label', 'Enviando imagem');

    const preview = document.createElement('img');
    preview.src = blobUrl;
    preview.alt = '';
    preview.draggable = false;
    preview.style.width = '100%';

    const overlay = document.createElement('div');
    overlay.className = 'cmc-notice-img-uploading-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<span class="cmc-notice-img-uploading-spinner"></span>' +
      '<span class="cmc-notice-img-uploading-label">Enviando…</span>';

    placeholder.appendChild(preview);
    placeholder.appendChild(overlay);

    try {
      if (range) {
        sel.removeAllRanges();
        sel.addRange(range);
        range.deleteContents();
        range.insertNode(placeholder);
        range.setStartAfter(placeholder);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        el.appendChild(placeholder);
      }
    } catch (_) {
      el.appendChild(placeholder);
    }

    this.setState({ saved: 'Enviando imagem…', slash: null, error: null });

    try {
      const uploaded = await api.uploadNoticeImage(n.id, file);
      if (!uploaded.guid || !uploaded.viewUrl) throw new Error('Resposta de upload incompleta.');

      const img = document.createElement('img');
      img.setAttribute('data-img', uploaded.guid);
      img.src = uploaded.viewUrl;
      img.style.width = '100%';

      if (placeholder.isConnected) {
        placeholder.replaceWith(img);
        const p = document.createElement('p');
        img.after(p);
      } else {
        el.focus();
        document.execCommand(
          'insertHTML',
          false,
          `<img data-img="${uploaded.guid}" src="${uploaded.viewUrl}" style="width: 100%;" /><p></p>`
        );
      }

      this._imgGuids.add(uploaded.guid);
      this.onEditorInput();
      this.setState({ saved: 'Salvo agora' });
    } catch (err) {
      if (placeholder.isConnected) placeholder.remove();
      this.setState({ saved: 'Erro ao salvar', error: err.message || 'Falha ao inserir imagem' });
    } finally {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch (_) {
        /* ignore */
      }
    }
  };

  startResize = (dir) => (e) => {
    e.preventDefault();
    const media = this.img;
    if (!media) return;
    const startX = e.clientX;
    const startW = media.getBoundingClientRect().width;
    const wrapW = this.editorRef.current.getBoundingClientRect().width;
    const fromLeft = dir === 'tl' || dir === 'bl';
    const move = (ev) => {
      const dx = (ev.clientX - startX) * (fromLeft ? -1 : 1);
      const w = Math.max(120, Math.min(wrapW, startW + dx));
      media.style.width = Math.round((w / wrapW) * 100) + '%';
      this.measure();
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      this.onEditorInput();
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  removeSelectedImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const media = this.img;
    const n = this.props.notice;
    if (!media || !n) return;
    const guid = media.getAttribute('data-img');
    if (media.parentNode) media.parentNode.removeChild(media);
    this.clearImgSelection();
    if (guid) {
      this._imgGuids.delete(guid);
      api.deleteNoticeImage(n.id, guid).catch(() => {});
    }
    this.onEditorInput();
  };

  openYoutubePrompt = () => {
    const el = this.editorRef.current;
    const sel = window.getSelection();
    this._ytRange = null;
    if (el && sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (this.selectionInEditor(r, el)) {
        try {
          this._ytRange = r.cloneRange();
        } catch (_) {
          this._ytRange = null;
        }
      }
    }
    this.setState({ ytPrompt: true, slash: null, ytError: null, error: null });
  };

  insertYoutube = () => {
    const el = this.editorRef.current;
    const scrollEl = this.docScrollRef.current;
    const id = parseYoutubeId(String(this.state.ytUrl || '').trim());
    if (!id) {
      this.setState({ ytError: 'Informe uma URL válida do YouTube.' });
      return;
    }
    const block = youtubeBlockHtml(id);
    if (!el || !block) {
      this.setState({ ytError: 'Não foi possível montar o embed.' });
      return;
    }

    const prevScroll = scrollEl ? scrollEl.scrollTop : 0;
    const holder = document.createElement('div');
    holder.innerHTML = `${block}<p><br></p>`;
    const frag = document.createDocumentFragment();
    while (holder.firstChild) frag.appendChild(holder.firstChild);
    const ytNode = frag.firstChild;
    const last = frag.lastChild;

    try {
      el.focus({ preventScroll: true });
    } catch (_) {
      el.focus();
    }
    const sel = window.getSelection();
    let range = this._ytRange;
    try {
      if (range && !this.selectionInEditor(range, el)) range = null;
    } catch (_) {
      range = null;
    }
    if (!range && sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (this.selectionInEditor(r, el)) range = r;
    }

    try {
      if (range && sel) {
        sel.removeAllRanges();
        sel.addRange(range);
        range.deleteContents();
        range.insertNode(frag);
        if (last) {
          const after = document.createRange();
          after.setStartAfter(last);
          after.collapse(true);
          sel.removeAllRanges();
          sel.addRange(after);
        }
      } else {
        el.appendChild(frag);
      }
    } catch (_) {
      el.appendChild(frag);
    }

    this._ytRange = null;
    this.setState({ ytPrompt: false, ytUrl: '', slash: null, ytError: null, error: null });
    this.onEditorInput();

    requestAnimationFrame(() => {
      if (scrollEl) {
        if (ytNode && ytNode.isConnected) {
          const parentRect = scrollEl.getBoundingClientRect();
          const nodeRect = ytNode.getBoundingClientRect();
          if (nodeRect.top < parentRect.top || nodeRect.bottom > parentRect.bottom) {
            ytNode.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else {
            scrollEl.scrollTop = prevScroll;
          }
        } else {
          scrollEl.scrollTop = prevScroll;
        }
      }
    });
  };

  runAction = async (kind) => {
    const n = this.props.notice;
    if (!n || this.state.busy) return;
    const canSchedule = isFutureLocalPublishAt(this.state.publishAt);
    if (kind === 'schedule' && !canSchedule) {
      this.setState({ error: 'Para agendar, defina “Exibir a partir de” em um horário futuro.' });
      return;
    }
    if (kind === 'schedule') {
      const conflict = this.findScheduleConflict(this.state.publishAt, this.state.expiresAt);
      if (conflict) {
        this.setState({
          error: `Já existe um aviso com período sobreposto (“${String(conflict.title || 'outro aviso').slice(0, 80)}”). Ajuste o início/término.`,
        });
        return;
      }
    }
    if (kind === 'publish' && canSchedule) {
      this.setState({
        error: 'Com data futura, use Agendar. Limpe ou ajuste “Exibir a partir de” para publicar agora.',
      });
      return;
    }
    if (kind === 'publish' && this.props.hasOtherInProgress) {
      this.setState({
        error: 'Já existe um aviso em andamento. Encerre-o antes de publicar outro.',
      });
      return;
    }
    this.setState({ busy: kind, error: null });
    try {
      await this.flushSave({ quiet: true });
      if (kind === 'publish') {
        await api.publishNotice(
          n.id,
          fromLocalInputValue(this.state.expiresAt),
          fromLocalInputValue(this.state.publishAt)
        );
      }
      if (kind === 'schedule') {
        await api.scheduleNotice(
          n.id,
          fromLocalInputValue(this.state.publishAt),
          fromLocalInputValue(this.state.expiresAt)
        );
        await this.loadScheduledSlots();
      }
      if (kind === 'archive') await api.archiveNotice(n.id);
      if (kind === 'delete') {
        await api.deleteNotice(n.id);
        this.props.onDeleted();
        return;
      }
      await this.props.onReload();
      this.setState({ busy: null, saved: 'Atualizado' });
    } catch (e) {
      this.setState({ busy: null, error: e.message || 'Falha na ação.' });
    }
  };

  render() {
    const s = this.state;
    const canSchedule = isFutureLocalPublishAt(s.publishAt);
    const blockedByOther = Boolean(this.props.hasOtherInProgress);
    const canPublishNow = !canSchedule && !blockedByOther;
    const scheduleTip = canSchedule
      ? ''
      : 'Defina “Exibir a partir de” em um horário futuro para agendar.';
    const publishTip = blockedByOther
      ? 'Já existe um aviso em andamento. Encerre-o antes de publicar outro.'
      : canSchedule
        ? 'Com data futura, use Agendar em vez de publicar agora.'
        : '';
    const statusLabel =
      s.status === 'published'
        ? 'Publicado'
        : s.status === 'scheduled'
          ? 'Agendado'
          : s.status === 'archived'
            ? 'Encerrado'
            : 'Rascunho';

    return (
      <div className="cmc-notice-editor">
        <div className="cmc-notice-editor-chrome">
        <div className="cmc-notice-editor-toolbar">
          <button type="button" className="board-clickup-filter-chip" onClick={this.props.onBack}>
            <span className="board-clickup-filter-chip-text">
              <span className="board-clickup-filter-chip-label">← Voltar</span>
            </span>
          </button>
          <span className={'cmc-notice-chip cmc-notice-chip--' + s.status}>
            {s.status === 'draft' ? (
              <Pencil size={11} strokeWidth={2} aria-hidden className="cmc-notice-chip-icon" />
            ) : null}
            {statusLabel}
          </span>
          <span className="cmc-manual-saved">{s.saved}</span>
          <div className="cmc-notice-editor-actions">
            <button
              type="button"
              className="board-clickup-filter-chip cmc-notice-chip-btn--danger"
              disabled={!!s.busy}
              onClick={() => this.runAction('delete')}
            >
              <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                <Trash2 size={13} strokeWidth={1.75} />
              </span>
              <span className="board-clickup-filter-chip-text">
                <span className="board-clickup-filter-chip-label">
                  {s.busy === 'delete' ? 'Excluindo…' : 'Excluir'}
                </span>
              </span>
            </button>
            <span className="cmc-notice-actions-pipe" aria-hidden />
            <div className="cmc-notice-publish-group" role="group" aria-label="Publicação">
              <CustomersNoticeTip label={scheduleTip} placement="bottom">
                <button
                  type="button"
                  className="board-clickup-filter-chip"
                  disabled={!!s.busy || !canSchedule}
                  onClick={() => this.runAction('schedule')}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <CalendarClock size={13} strokeWidth={1.75} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">
                      {s.busy === 'schedule' ? 'Agendando…' : 'Agendar'}
                    </span>
                  </span>
                </button>
              </CustomersNoticeTip>
              <CustomersNoticeTip label={publishTip} placement="bottom">
                <button
                  type="button"
                  className="board-clickup-filter-chip has-selection"
                  disabled={!!s.busy || !canPublishNow}
                  onClick={() => this.runAction('publish')}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <Send size={13} strokeWidth={1.75} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">
                      {s.busy === 'publish' ? 'Publicando…' : 'Publicar agora'}
                    </span>
                  </span>
                </button>
              </CustomersNoticeTip>
            </div>
          </div>
        </div>

        <div className="cmc-notice-schedule">
          <CustomersNoticeScheduleDates
            publishAt={s.publishAt}
            expiresAt={s.expiresAt}
            scheduledSlots={s.scheduledSlots || []}
            onOpen={this.loadScheduledSlots}
            onChange={({ publishAt, expiresAt }) => {
              this.setState({ publishAt, expiresAt }, () => {
                if (this.state.status !== 'archived' || !publishAt || this.state.busy) return;
                const kind = isFutureLocalPublishAt(publishAt) ? 'schedule' : 'publish';
                this.runAction(kind);
              });
            }}
          />
          {s.status === 'published' ? (
            <button
              type="button"
              className="board-clickup-filter-chip"
              disabled={!!s.busy}
              onClick={() => this.runAction('archive')}
            >
              <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                <CircleStop size={13} strokeWidth={1.75} />
              </span>
              <span className="board-clickup-filter-chip-text">
                <span className="board-clickup-filter-chip-label">
                  {s.busy === 'archive' ? 'Encerrando…' : 'Encerrar aviso'}
                </span>
              </span>
            </button>
          ) : null}
        </div>

        {s.error ? <div className="cmc-notice-error">{s.error}</div> : null}
        </div>

        <div className="cmc-notice-editor-doc" ref={this.docScrollRef}>
        <div className="cmc-manual-editor-wrap">
          <input
            className="cmc-manual-editor-title"
            value={s.title}
            onChange={this.onTitleChange}
            placeholder="Título do aviso"
          />
          <div className="cmc-manual-editor-hints cmc-manual-grad-text">
            Digite / para blocos · Selecione o texto para formatar · Clique na imagem e arraste as
            bordas · Ctrl+V cola imagem ou texto limpo · YouTube via /
          </div>
          <div
            ref={this.editorRef}
            className="cmc-manual-editor"
            contentEditable
            suppressContentEditableWarning
            data-ph='Escreva, pressione a barra de espaço para usar a IA ou "/" para usar comandos'
            onInput={() => {
              this.onEditorInput();
              this.syncLineHint();
            }}
            onPaste={this.onEditorPaste}
            onKeyDown={this.onEditorKeyDown}
            onKeyUp={this.onEditorKey}
            onPointerDown={this.onEditorPointerDown}
            onPointerUp={this.onEditorPointerUp}
            onMouseUp={this.onEditorPointerUp}
            onClick={this.onEditorClick}
          />
        </div>
        </div>

        {s.lineHint
          ? createPortal(
              <div
                className="cmc-notice-line-hint"
                style={{ top: s.lineHint.top, left: s.lineHint.left }}
                aria-hidden
              >
                Escreva, pressione a barra de espaço para usar a IA ou &quot;/&quot; para usar comandos
              </div>,
              document.body
            )
          : null}

        {s.oryaCompose
          ? createPortal(
              <CustomersNoticeOryaCompose
                position={{ top: s.oryaCompose.top, left: s.oryaCompose.left }}
                noticeTitle={s.title}
                onInsert={this.insertOryaComposeText}
                onClose={this.closeOryaCompose}
              />,
              document.body
            )
          : null}

        {s.imgBox
          ? createPortal(
              <div
                className="cmc-manual-img-box"
                style={{
                  top: s.imgBox.top,
                  left: s.imgBox.left,
                  width: s.imgBox.w,
                  height: s.imgBox.h,
                }}
              >
                <div className="cmc-manual-img-handle cmc-manual-img-handle--tl" onMouseDown={this.startResize('tl')} />
                <div className="cmc-manual-img-handle cmc-manual-img-handle--tr" onMouseDown={this.startResize('tr')} />
                <div className="cmc-manual-img-handle cmc-manual-img-handle--bl" onMouseDown={this.startResize('bl')} />
                <div className="cmc-manual-img-handle cmc-manual-img-handle--br" onMouseDown={this.startResize('br')} />
                <div className="cmc-manual-img-size">
                  {Math.round(s.imgBox.w)} × {Math.round(s.imgBox.h)}
                </div>
                <CustomersNoticeTip
                  label={this.img?.hasAttribute?.('data-yt') ? 'Excluir vídeo' : 'Excluir imagem'}
                  placement="top"
                >
                  <button
                    type="button"
                    className="cmc-manual-img-delete"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                    }}
                    onClick={this.removeSelectedImage}
                  >
                    Excluir
                  </button>
                </CustomersNoticeTip>
              </div>,
              document.body
            )
          : null}

        {s.fmt
          ? createPortal(
              <CustomersNoticeFmtBar
                position={{ top: s.fmt.top, left: s.fmt.left }}
                selectedText={s.fmt.text || ''}
                linkHref={s.fmt.linkHref || ''}
                onExec={(cmd, val) => this.exec(cmd, val)}
                onReplaceSelection={this.replaceFmtSelection}
                onInsertBelow={this.insertFmtBelow}
                onClose={() => {
                  this.setState({ fmt: null });
                  this._fmtRange = null;
                }}
              />,
              document.body
            )
          : null}

        {s.slash
          ? createPortal(
              <div
                className="cmc-manual-slash"
                style={clampFixedPanelCoords({
                  preferredTop: s.slash.top,
                  preferredLeft: s.slash.left,
                  width: 280,
                  height: 360,
                })}
              >
                <div className="cmc-manual-slash-title">Inserir bloco</div>
                {[
                  { label: 'Título 1', hint: 'Seção principal', run: () => this.exec('formatBlock', 'H1') },
                  { label: 'Título 2', hint: 'Subseção', run: () => this.exec('formatBlock', 'H2') },
                  { label: 'Lista', hint: 'Passo a passo', run: () => this.exec('insertUnorderedList') },
                  { label: 'Imagem', hint: 'Redimensionável', run: this.insertImage },
                  { label: 'YouTube', hint: 'Vídeo embed', run: this.openYoutubePrompt },
                  { label: 'Código', hint: 'Bloco monoespaçado', run: () => this.exec('formatBlock', 'PRE') },
                  { label: 'Aviso', hint: 'Citação destacada', run: () => this.exec('formatBlock', 'BLOCKQUOTE') },
                  { label: 'Divisor', hint: 'Linha horizontal', run: () => this.exec('insertHorizontalRule') },
                ].map((it) => (
                  <div
                    key={it.label}
                    className="cmc-manual-slash-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      this.setState({ slash: null });
                      it.run();
                    }}
                  >
                    <span>{it.label}</span>
                    <span>{it.hint}</span>
                  </div>
                ))}
              </div>,
              document.body
            )
          : null}

        {s.ytPrompt
          ? createPortal(
              <div
                className="cmc-manual-modal-scrim"
                onClick={() => this.setState({ ytPrompt: false, ytError: null })}
              >
                <div className="cmc-manual-modal cmc-manual-modal--sm" onClick={(e) => e.stopPropagation()}>
                  <div className="cmc-manual-modal-title">Vídeo do YouTube</div>
                  <div className="cmc-manual-modal-sub">Cole a URL do vídeo para incorporar no aviso.</div>
                  <input
                    className="cmc-manual-input"
                    value={s.ytUrl}
                    placeholder="https://www.youtube.com/watch?v=…"
                    autoFocus
                    onChange={(e) => this.setState({ ytUrl: e.target.value, ytError: null })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        this.insertYoutube();
                      }
                    }}
                  />
                  {s.ytError ? <div className="cmc-notice-error">{s.ytError}</div> : null}
                  <div className="cmc-manual-modal-actions">
                    <button
                      type="button"
                      className="cmc-manual-ghost-btn cmc-manual-ghost-btn--md"
                      onClick={() => this.setState({ ytPrompt: false, ytError: null })}
                    >
                      Cancelar
                    </button>
                    <button type="button" className="cmc-manual-primary-btn" onClick={this.insertYoutube}>
                      Inserir
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }
}
